import { deiterateAPI } from '../js/external-js/variableModules/utils/deiterateAPI.util';

type IsModuleEnabled = (moduleName: string) => boolean;

// Define interface for control data
export interface ControlData {
  value: string;        // controlID
  label: string;        // controlName
  description: string;  // controlDescription
  standard: string;     // Which standard it belongs to
  metadata: Record<string, any>; // Additional metadata
}

// DED-1995: Framework-qualified control reference
export interface QualifiedControlRef {
  controlId: string;
  framework: string;
}

/**
 * DED-1995: Create a qualified control ID string from control ID and framework
 * Format: "FRAMEWORK:CONTROL_ID" e.g., "ISO 14001:6.2"
 */
export const createQualifiedControlId = (controlId: string, framework: string): string => {
  if (!framework) return controlId;
  return `${framework}:${controlId}`;
};

/**
 * DED-1995: Parse a control reference that may be legacy (string) or qualified (object)
 * Returns a QualifiedControlRef with controlId and framework (framework may be null for legacy)
 */
export const parseControlRef = (ref: string | QualifiedControlRef | any): QualifiedControlRef | null => {
  if (!ref) return null;
  
  // Handle object format { controlId, framework }
  if (typeof ref === 'object' && ref.controlId) {
    return {
      controlId: String(ref.controlId),
      framework: ref.framework ? String(ref.framework) : ''
    };
  }
  
  // Handle qualified string format "FRAMEWORK:CONTROL_ID"
  if (typeof ref === 'string' && ref.includes(':')) {
    const colonIndex = ref.indexOf(':');
    const framework = ref.substring(0, colonIndex);
    const controlId = ref.substring(colonIndex + 1);
    // Only treat as qualified if the framework part looks like a standard name
    if (framework && (framework.includes('ISO') || framework.includes('SOC') || framework.includes('NIST') || framework.includes('Essential') || framework.includes('Privacy') || framework.includes('DISP') || framework.includes('ISM') || framework.includes('SMB') || framework.includes('QGOV'))) {
      return { controlId, framework };
    }
  }
  
  // Handle legacy string format (just control ID)
  if (typeof ref === 'string') {
    return { controlId: ref, framework: '' };
  }
  
  return null;
};

/**
 * DED-1995: Convert an array of control refs (mixed legacy/new) to qualified refs
 * If framework context is provided, use it to qualify legacy refs
 */
export const normalizeControlRefs = (
  refs: (string | QualifiedControlRef | any)[],
  defaultFramework?: string
): QualifiedControlRef[] => {
  if (!Array.isArray(refs)) return [];
  
  return refs
    .map(ref => {
      const parsed = parseControlRef(ref);
      if (!parsed) return null;
      
      // If no framework and we have a default, use it
      if (!parsed.framework && defaultFramework) {
        return { ...parsed, framework: defaultFramework };
      }
      
      return parsed;
    })
    .filter((ref): ref is QualifiedControlRef => ref !== null);
};

/**
 * DED-1995: Get the display value for a control (used in combobox)
 * Returns the qualified ID for unique identification
 */
export const getControlDisplayValue = (control: ControlData): string => {
  return createQualifiedControlId(control.value, control.standard);
};

/**
 * DED-1995: Check if a control matches a qualified reference
 */
export const controlMatchesRef = (control: ControlData, ref: QualifiedControlRef): boolean => {
  // Control ID must match
  if (control.value !== ref.controlId) return false;
  
  // If ref has no framework, match any control with this ID
  if (!ref.framework) return true;
  
  // Framework must match (case-insensitive)
  return control.standard.toLowerCase() === ref.framework.toLowerCase();
};

// Cache for controls to avoid repeated API calls
let controlsCache: Record<string, ControlData[]> = {};
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// API base path
const CONTROLS_API = '/v1/controls';

// Define all possible standards
const ALL_STANDARDS = [
  'ISO 27001',
  'SOC 2',
  'NIST 800-53',
  'Essential 8',
  'Privacy Act',
  'DISP',
  'ISM',
  'QGOV-IS18',
  'SMB1001',
  'ISO 9001',
  'ISO 45001',
  'ISO 14001',
  'ISO 42001',
  'ISO 27701'
];

/**
 * Get clauses for a specific ISO standard (if available)
 */
export const getClausesByStandard = async (standard: string): Promise<ControlData[]> => {
  try {
    const companyID = (window as any).companyID;
    const res = await deiterateAPI.GET(`${CONTROLS_API}/clauses/${encodeURIComponent(standard)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });
    return res.clauses || [];
  } catch (e) {
    console.error('[ControlsService] Error getting clauses for', standard, e);
    return [];
  }
};

/**
 * Get all standards available in the system
 * @param isModuleEnabled Function to check if a module is enabled
 * @returns Array of standard names
 */
export const getStandards = async (isModuleEnabled: IsModuleEnabled): Promise<string[]> => {
  // console.log('[ControlsService] getStandards called with isModuleEnabled:', !!isModuleEnabled);

  try {
    if (!isModuleEnabled) {
      console.warn('[ControlsService] isModuleEnabled function not provided, returning empty standards list');
      return [];
    }

    // Log all standards being checked
    // console.log('[ControlsService] Checking all standards:', ALL_STANDARDS);

    // Filter standards based on enabled modules
    const filtered = ALL_STANDARDS.filter(standard => {
      // Map standard names to their module names
      const moduleMap: Record<string, string> = {
        'ISO 27001': 'iso27001',
        'SOC 2': 'soc2',
        'NIST 800-53': 'nist80053',
        'Essential 8': 'essential8',
        'Privacy Act': 'privacyact',
        'DISP': 'disp',
        'ISM': 'ism',
        'SMB1001': 'smb1001',
        'qgov-is18': 'qgov-is18',
        'QGOV-IS18': 'qgov-is18',
        'ISO 9001': 'iso9001',
        'ISO 45001': 'iso45001',
        'ISO 14001': 'iso14001',
        'ISO 42001': 'iso42001',
        'ISO 27701': 'iso27701'
      };

      const moduleName = moduleMap[standard];
      const isEnabled = moduleName ? isModuleEnabled(moduleName) : false;

      // console.log(`[ControlsService] Checking standard: ${standard}`, {
      //   moduleName,
      //   isEnabled,
      //   isModuleEnabled: !!isModuleEnabled
      // });

      return isEnabled;
    });

    // console.log('[ControlsService] Filtered standards:', filtered);
    return filtered;
  } catch (error) {
    console.error('[ControlsService] Error getting standards:', error);
    return [];
  }
};

/**
 * Get all enabled standards for the current company
 * @param enabledModules Array of enabled module names
 * @returns Array of enabled standard names
 */
export const getEnabledStandards = async (enabledModules: string[] = []): Promise<string[]> => {
  try {
    if (!Array.isArray(enabledModules)) {
      console.warn('[ControlsService] enabledModules is not an array, returning empty standards list');
      return [];
    }

    // Map between module names and their display names
    const moduleToStandardMap: Record<string, string> = {
      'iso27001': 'ISO 27001',
      'soc2': 'SOC 2',
      'nist80053': 'NIST 800-53',
      'essential8': 'Essential 8',
      'privacyact': 'Privacy Act',
      'disp': 'DISP',
      'ism': 'ISM',
      'smb1001': 'SMB1001',
      'qgov-is18': 'QGOV-IS18',
      'QGOV-IS18': 'QGOV-IS18',
      'iso9001': 'ISO 9001',
      'iso45001': 'ISO 45001',
      'iso14001': 'ISO 14001',
      'iso42001': 'ISO 42001',
      'iso27701': 'ISO 27701'
    };

    // Convert enabled modules to standard names
    const standards = enabledModules
      .map(module => moduleToStandardMap[module.toLowerCase()])
      .filter(Boolean) as string[]; // Filter out any undefined values

    // console.log('[ControlsService] Enabled standards:', standards);
    return standards;
  } catch (error) {
    console.error('[ControlsService] Error getting enabled standards:', error);
    return [];
  }
};

/**
 * Gets both controls and clauses for a given standard
 * @param standard The standard name
 * @returns Combined array of controls and clauses
 */
export const getControlsAndClauses = async (standard: string): Promise<ControlData[]> => {
  const [controls, clauses] = await Promise.all([
    getControlsByStandard(standard),
    getISOClauses(standard)
  ]);

  // Combine and deduplicate by control ID
  const combined = [...controls];
  const existingIds = new Set(controls.map(c => c.value));

  for (const clause of clauses) {
    if (!existingIds.has(clause.value)) {
      combined.push(clause);
    }
  }

  return combined;
};


/**
 * Fetches and processes ISO clauses for a given standard via Thalassa API
 * @param standard The ISO standard name (e.g., 'ISO 9001', 'ISO 27001')
 * @returns Array of ControlData objects for the clauses
 */
const getISOClauses = async (standard: string): Promise<ControlData[]> => {
  try {
    const companyID = (window as any).companyID;
    const res = await deiterateAPI.GET(`${CONTROLS_API}/clauses/${encodeURIComponent(standard)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });
    return res.clauses || [];
  } catch (error) {
    console.error(`Error fetching clauses for ${standard}:`, error);
    return [];
  }
};

/**
 * Get controls for a specific standard via Thalassa API
 * @param standard Standard name to fetch controls for
 * @returns Array of control data
 */
export const getControlsByStandard = async (standard: string): Promise<ControlData[]> => {
  // If we have cached controls for this standard and they're still fresh, return them
  if (controlsCache[standard] && Date.now() - lastFetchTime < CACHE_DURATION) {
    return controlsCache[standard];
  }

  try {
    const companyID = (window as any).companyID;
    const res = await deiterateAPI.GET(`${CONTROLS_API}/by-standard/${encodeURIComponent(standard)}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });

    const controls: ControlData[] = res.controls || [];

    // Cache the results
    controlsCache[standard] = controls;
    lastFetchTime = Date.now();

    return controls;
  } catch (error) {
    console.error(`[ControlsService] Error fetching controls for ${standard}:`, error);
    return [];
  }
};

/**
 * Get all controls for all enabled standards
 * @param isModuleEnabled Function to check if a module is enabled
 * @returns Array of control data
 */
export const getAllControls = async (isModuleEnabled: IsModuleEnabled): Promise<ControlData[]> => {
  try {
    // Check if we have a complete cache
    const allCachedControls = Object.values(controlsCache).flat();
    if (allCachedControls.length > 0 && Date.now() - lastFetchTime < CACHE_DURATION) {
      // console.log('[ControlsService] Returning cached controls:', {
      //   count: allCachedControls.length,
      //   allCachedControls,
      //   lastFetchTime,
      //   now: Date.now(),
      //   cacheDuration: CACHE_DURATION
      // });
      return allCachedControls;
    }

    // Get all available standards, not just enabled ones
    // This ensures we have a complete dataset
    const standards = await getStandards(isModuleEnabled);
    const allControls: ControlData[] = [];

    // Fetch controls for each standard
    for (const standard of standards) {
      // console.log(`[ControlsService] Fetching controls for ${standard}`);
      const controls = await getControlsByStandard(standard);
      // console.log(`[ControlsService] Found ${controls.length} controls for ${standard}`);
      allControls.push(...controls);
    }

    // console.log(`[ControlsService] Total controls found: ${allControls.length}`);
    // console.log('[ControlsService] Controls:', allControls);    
    return allControls;
  } catch (error) {
    console.error('[ControlsService] Error fetching all controls:', error);
    return [];
  }
};

// In-memory search index to speed up searches
let searchIndex: Map<string, Set<string>> = new Map();
let isIndexBuilt = false;

/**
 * Build a search index for faster searches
 */
const buildSearchIndex = (controls: ControlData[]) => {
  if (isIndexBuilt) return;

  // console.log('[ControlsService] Building search index for', controls.length, 'controls');
  searchIndex.clear();

  // Process each control
  controls.forEach(control => {
    // Skip invalid controls
    if (!control.value) return;

    // Helper function to index a field
    const indexField = (field: string | undefined, isControlId: boolean = false) => {
      if (!field) return;

      // For control IDs, we want to preserve the full ID as a token
      // as well as index its parts for more flexible searching
      if (isControlId) {
        // Add the complete control ID as a token (preserving dots, numbers, etc.)
        const fullId = field.toLowerCase();
        if (!searchIndex.has(fullId)) {
          searchIndex.set(fullId, new Set());
        }
        searchIndex.get(fullId)?.add(control.value);
      }

      // Tokenize the field (split into words)
      const tokens = field.toLowerCase()
        .replace(/[^\w\s]/g, ' ') // Replace non-alphanumeric with spaces
        .split(/\s+/)
        .filter(token => token.length > 2); // Only index tokens with 3+ chars

      // Add each token to the index
      tokens.forEach(token => {
        if (!searchIndex.has(token)) {
          searchIndex.set(token, new Set());
        }
        searchIndex.get(token)?.add(control.value);
      });
    };

    // Index the main fields
    indexField(control.value, true); // Mark control.value as a control ID
    indexField(control.label);
    indexField(control.description);

    // Index metadata fields that are strings
    if (control.metadata && typeof control.metadata === 'object') {
      Object.values(control.metadata).forEach(value => {
        if (typeof value === 'string') {
          indexField(value);
        }
      });
    }
  });

  // console.log('[ControlsService] Search index built with', searchIndex.size, 'tokens');
  isIndexBuilt = true;
};

/**
 * Reset the search index when data changes
 */
export const resetSearchIndex = () => {
  searchIndex.clear();
  isIndexBuilt = false;
};

/**
 * Search for controls based on a query
 * @param query Search query
 * @param standardFilter Optional standard to filter by
 * @param page Optional page number for pagination (1-based)
 * @param pageSize Optional page size for pagination
 * @returns Object with results array and pagination info
 */
export const searchControls = async (
  query: string,
  isModuleEnabled: IsModuleEnabled,
  standardFilter?: string,
  page: number = 1,
  pageSize: number = 50,
): Promise<{ results: ControlData[], total: number, page: number, pageSize: number, pages: number }> => {
  try {
    // console.log(`[ControlsService] Searching controls with query: "${query}"${standardFilter ? ` in standard: ${standardFilter}` : ''}`);
    const startTime = performance.now();

    // Get all controls or filtered by standard
    let controls: ControlData[] = [];

    if (standardFilter && typeof standardFilter === 'string') {
      // console.log(`[ControlsService] Fetching controls and clauses for standard: ${standardFilter}`);
      
      // Get both controls and clauses for the standard
      const [standardControls, standardClauses] = await Promise.all([
        getControlsByStandard(standardFilter).catch(err => {
          console.error(`[ControlsService] Error fetching controls for ${standardFilter}:`, err);
          return [];
        }),
        // Check if this is an ISO standard that might have clauses
        standardFilter.startsWith('ISO ') ? getISOClauses(standardFilter).catch(err => {
          console.error(`[ControlsService] Error fetching clauses for ${standardFilter}:`, err);
          return [];
        }) : Promise.resolve([])
      ]);
      
      // Combine and deduplicate controls and clauses by value
      const combined = [...standardControls];
      const existingValues = new Set(standardControls.map(c => c.value));
      
      for (const clause of standardClauses) {
        if (!existingValues.has(clause.value)) {
          combined.push(clause);
          existingValues.add(clause.value);
        }
      }
      
      controls = combined;
      // console.log(`[ControlsService] Found ${standardControls.length} controls and ${standardClauses.length} clauses for standard: ${standardFilter} (${controls.length} total)`);
    } else {
      // Otherwise, get all controls from all standards
      controls = await getAllControls(isModuleEnabled);
      // console.log(`[ControlsService] Found ${controls.length} controls across all standards`);
    }

    // If no query, return paginated controls (filtered by standard if specified)
    if (!query || !query.trim()) {
      const total = controls.length;
      const pages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, total);

      return {
        results: controls.slice(startIndex, endIndex),
        total,
        page,
        pageSize,
        pages
      };
    }

    // Simple direct search for all queries
    const searchQuery = query.toLowerCase();
    const matches = new Set<string>();

    // Log what we're searching for
    // console.log(`[ControlsService] Performing direct search for: "${searchQuery}"`);

    controls.forEach(control => {
      if (!control.value) return;

      // Check if any field contains the search query
      const controlIdLower = control.value.toLowerCase();
      const labelLower = control.label ? control.label.toLowerCase() : '';
      const descLower = control.description ? control.description.toLowerCase() : '';

      // For debugging
      if (descLower.includes(searchQuery)) {
        // console.log(`[ControlsService] Found match in description: ${control.value} - ${control.description}`);
      }
      if (labelLower.includes(searchQuery)) {
        // console.log(`[ControlsService] Found match in label: ${control.value} - ${control.label}`);
      }

      if (
        controlIdLower.includes(searchQuery) ||
        labelLower.includes(searchQuery) ||
        descLower.includes(searchQuery)
      ) {
        matches.add(control.value);
      }
    });

    let matchingControlIds = matches;

    // If no matches, return empty result
    if (!matchingControlIds || matchingControlIds.size === 0) {
      return { results: [], total: 0, page, pageSize, pages: 0 };
    }

    // Convert matching IDs to actual control objects
    const matchingControls = controls.filter(control =>
      control.value && matchingControlIds?.has(control.value)
    );

    // Apply pagination
    const total = matchingControls.length;
    const pages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);
    const paginatedResults = matchingControls.slice(startIndex, endIndex);

    const endTime = performance.now();
    // console.log(`[ControlsService] Search found ${total} matching controls in ${(endTime - startTime).toFixed(2)}ms`);

    return {
      results: paginatedResults,
      total,
      page,
      pageSize,
      pages
    };
  } catch (error) {
    console.error('[ControlsService] Error searching controls:', error);
    return { results: [], total: 0, page, pageSize, pages: 0 };
  }
};

/**
 * Find controls by their IDs
 * @param controlIds Array of control IDs to find
 * @returns Array of matching control data
 */
export const findControlsByIds = async (controlIds: string[], isModuleEnabled: IsModuleEnabled): Promise<ControlData[]> => {
  try {
    if (!isModuleEnabled) {
      console.warn('[ControlsService] isModuleEnabled function not provided, cannot find controls by IDs');
      return [];
    }

    const allControls = await getAllControls(isModuleEnabled);
    return allControls.filter(control => control.value && controlIds.includes(control.value));
  } catch (error) {
    console.error('[ControlsService] Error finding controls by IDs:', error);
    return [];
  }
};

/**
 * Clear the controls cache to force a fresh fetch
 */
export const clearControlsCache = () => {
  controlsCache = {};
  lastFetchTime = 0;
};

export default {
  getStandards,
  getEnabledStandards,
  getControlsAndClauses,
  getClausesByStandard,
  getControlsByStandard,
  getAllControls,
  searchControls,
  findControlsByIds,
  clearControlsCache,
  // DED-1995: Framework-qualified control helpers
  createQualifiedControlId,
  parseControlRef,
  normalizeControlRefs,
  getControlDisplayValue,
  controlMatchesRef
};
